import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { ChevronLeft, ChevronRight, CalendarDays, Plus, Trash2, Pencil, Clock, Contact, X, Scissors, User, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { DOW_LABELS, MONTH_LABELS, buildMonthGrid, findConflicts, computeOverlappingIds, rdvVisualStatus, useNow } from "../../lib/calendarUtils";

// Classes Tailwind par statut visuel (voir calendarUtils.js > rdvVisualStatus)
// — mêmes couleurs que dans l'onglet Vente (CaissePage.jsx) et le tableau de
// bord (Dashboard.jsx > MonthAgenda), pour rester cohérent partout où un
// rendez-vous peut s'afficher.
const RDV_STATUS_STYLES = {
  termine: { chip: "bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 ring-1 ring-emerald-500/60", card: "border-emerald-500/60 bg-emerald-500/10", label: "Terminé (vente clôturée)" },
  en_retard: { chip: "bg-red-500/20 text-red-700 dark:text-red-400 ring-1 ring-red-500/60", card: "border-red-500/60 bg-red-500/10", label: "En retard — vente non clôturée" },
  superposition: { chip: "bg-violet-500/20 text-violet-700 dark:text-violet-400 ring-1 ring-violet-500/60", card: "border-violet-500/60 bg-violet-500/10", label: "Superposition avec un autre rendez-vous" },
  normal: { chip: "bg-amber-500/15 text-amber-700 dark:text-amber-400", card: "border-border", label: null },
};

// Onglet "Prise de rdv" de la Caisse (modèle de vente "rendez_vous", voir
// Administrateurs > Boutiques > Modèle de l'onglet Vente) : calendrier par
// mois — cliquer un jour affiche les rendez-vous déjà pris ce jour-là (heure,
// prestation(s), durée, prix), permet d'en ajouter un nouveau ou de modifier
// un rendez-vous existant. Réutilise le même agenda que le widget du tableau
// de bord (routers/rendezvous_routes.py, collection `rendez_vous`) — un
// rendez-vous pris ici apparaît donc aussi dans l'agenda du tableau de bord,
// et réciproquement.
//
// Superposition : deux rendez-vous d'un MÊME vendeur dont les créneaux se
// chevauchent (voir lib/calendarUtils.js) sont mis en évidence en violet, à
// la fois dans les pastilles du calendrier et dans le panneau du jour — et un
// avertissement bloque l'enregistrement d'un nouveau chevauchement tant que
// l'utilisateur ne l'a pas explicitement confirmé.
export function PriseRdvTab({ articles }) {
  const { user } = useAuth();
  const today = new Date();
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [rendezVous, setRendezVous] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(today);
  const [formOpen, setFormOpen] = useState(false);
  const [editingRdv, setEditingRdv] = useState(null);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const weeks = useMemo(() => buildMonthGrid(year, month), [year, month]);

  const canAssignOthers = hasPerm(user, "caisse", "assigner_rdv");
  const canSearchClients = hasPerm(user, "client", "view");
  const serviceArticles = useMemo(() => (articles || []).filter((a) => a.type === "service"), [articles]);
  const overlappingIds = useMemo(() => computeOverlappingIds(rendezVous), [rendezVous]);
  // Horloge vive : un rendez-vous doit passer tout seul en rouge ("en
  // retard") dès que l'heure de fin prévue est dépassée, sans action de
  // l'utilisateur — voir calendarUtils.js > useNow/rdvVisualStatus.
  const now = useNow();
  const statusFor = (r) => rdvVisualStatus(r, { overlapping: overlappingIds.has(r.id), now });

  const load = async () => {
    setLoading(true);
    try {
      const monthStart = new Date(year, month, 1);
      const monthEnd = new Date(year, month + 1, 0, 23, 59, 59);
      const { data } = await api.get("/rendez-vous", { params: { date_from: monthStart.toISOString(), date_to: monthEnd.toISOString() } });
      setRendezVous(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const isToday = (d) => d.toDateString() === today.toDateString();
  const isCurrentMonth = (d) => d.getMonth() === month;
  const isSelected = (d) => selectedDay && d.toDateString() === selectedDay.toDateString();

  const rdvForDay = (d) => {
    if (!d) return [];
    const dayKey = d.toDateString();
    return rendezVous
      .filter((r) => new Date(r.date_heure).toDateString() === dayKey)
      .sort((a, b) => new Date(a.date_heure) - new Date(b.date_heure));
  };

  const selectedDayRdv = rdvForDay(selectedDay);

  const deleteRdv = async (rdvId) => {
    try {
      await api.delete(`/rendez-vous/${rdvId}`);
      toast.success("Rendez-vous supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openAdd = () => { setEditingRdv(null); setFormOpen(true); };
  const openEdit = (r) => { setEditingRdv(r); setFormOpen(true); };

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_360px]" data-testid="prise-rdv-tab">
      <div className="rounded-xl border border-border bg-card p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewDate(new Date(year, month - 1, 1))}
              className="p-1.5 rounded-lg border border-border hover:bg-accent transition-colors"
              data-testid="prise-rdv-prev-month"
              aria-label="Mois précédent"
            >
              <ChevronLeft size={16} />
            </button>
            <h3 className="font-heading font-semibold flex items-center gap-2 min-w-[170px] justify-center">
              <CalendarDays size={16} className="text-primary" /> {MONTH_LABELS[month]} {year}
            </h3>
            <button
              onClick={() => setViewDate(new Date(year, month + 1, 1))}
              className="p-1.5 rounded-lg border border-border hover:bg-accent transition-colors"
              data-testid="prise-rdv-next-month"
              aria-label="Mois suivant"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <Button size="sm" className="gap-1.5" onClick={openAdd} data-testid="prise-rdv-add-button">
            <Plus size={14} />Ajouter un rdv
          </Button>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground">Chargement...</p>
        ) : (
          <>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {DOW_LABELS.map((d) => (
                <p key={d} className="text-center text-[11px] font-semibold text-muted-foreground">{d}</p>
              ))}
            </div>
            <div className="space-y-1">
              {weeks.map((week, wi) => (
                <div key={wi} className="grid grid-cols-7 gap-1">
                  {week.map((d, di) => {
                    const events = rdvForDay(d);
                    return (
                      <button
                        key={di}
                        onClick={() => setSelectedDay(d)}
                        className={`min-h-[64px] text-left rounded-lg border p-1 space-y-0.5 transition-colors ${
                          isSelected(d) ? "border-primary bg-primary/10" : isToday(d) ? "border-primary/50 bg-primary/5" : "border-border hover:bg-accent/50"
                        } ${!isCurrentMonth(d) ? "opacity-40" : ""}`}
                        data-testid={`prise-rdv-day-${d.toISOString().slice(0, 10)}`}
                      >
                        <p className={`text-[11px] font-medium ${isToday(d) ? "text-primary" : ""}`}>{d.getDate()}</p>
                        {events.slice(0, 2).map((ev) => {
                          const status = statusFor(ev);
                          return (
                            <p
                              key={ev.id}
                              className={`text-[9px] leading-tight truncate rounded px-1 py-0.5 ${RDV_STATUS_STYLES[status].chip}`}
                              data-testid={status !== "normal" ? `prise-rdv-chip-${status}-${ev.id}` : undefined}
                            >
                              {new Date(ev.date_heure).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} {ev.client_nom || ev.titre}
                            </p>
                          );
                        })}
                        {events.length > 2 && (
                          <p className="text-[9px] text-muted-foreground pl-1">+{events.length - 2} autre(s)</p>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="rounded-xl border border-border bg-card p-5" data-testid="prise-rdv-day-panel">
        <h3 className="font-heading font-semibold mb-3">
          {selectedDay ? `${selectedDay.getDate()} ${MONTH_LABELS[selectedDay.getMonth()]} ${selectedDay.getFullYear()}` : "Sélectionnez un jour"}
        </h3>
        {!selectedDay ? (
          <p className="text-sm text-muted-foreground">Cliquez sur un jour du calendrier pour voir ses rendez-vous.</p>
        ) : selectedDayRdv.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aucun rendez-vous ce jour-là.</p>
        ) : (
          <div className="space-y-2 max-h-[520px] overflow-y-auto">
            {selectedDayRdv.map((r) => {
              const status = statusFor(r);
              const style = RDV_STATUS_STYLES[status];
              return (
                <div
                  key={r.id}
                  className={`rounded-lg border p-3 text-sm space-y-1 ${style.card}`}
                  data-testid={`prise-rdv-item-${r.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium">
                      {new Date(r.date_heure).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                      {r.client_nom ? ` — ${r.client_nom}` : ""}
                    </p>
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => openEdit(r)} className="text-muted-foreground hover:text-foreground" title="Modifier" data-testid={`prise-rdv-edit-${r.id}`}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => deleteRdv(r.id)} className="text-destructive" title="Supprimer" data-testid={`prise-rdv-delete-${r.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  {style.label && (
                    <p
                      className={`flex items-center gap-1 text-[11px] font-medium ${
                        status === "termine" ? "text-emerald-700 dark:text-emerald-400"
                          : status === "en_retard" ? "text-red-700 dark:text-red-400"
                          : "text-violet-700 dark:text-violet-400"
                      }`}
                      data-testid={`prise-rdv-status-badge-${r.id}`}
                    >
                      {status === "termine" ? <CheckCircle2 size={11} /> : <AlertTriangle size={11} />} {style.label}
                    </p>
                  )}
                  {r.prestations && r.prestations.length > 0 ? (
                    <p className="text-muted-foreground text-xs">{r.prestations.map((p) => p.nom).join(", ")}</p>
                  ) : (
                    <p className="text-muted-foreground text-xs">{r.titre}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {r.duree_minutes != null && (
                      <span className="flex items-center gap-1"><Clock size={11} /> {r.duree_minutes >= 60 ? `${Math.floor(r.duree_minutes / 60)}h${r.duree_minutes % 60 ? String(r.duree_minutes % 60).padStart(2, "0") : ""}` : `${r.duree_minutes} min`}</span>
                    )}
                    {r.prix_ttc != null && <span>{Number(r.prix_ttc).toFixed(2)} €</span>}
                    {r.vendeur_nom && <span className="flex items-center gap-1"><User size={11} /> {r.vendeur_nom}</span>}
                  </div>
                  {r.notes && <p className="text-xs text-muted-foreground italic">{r.notes}</p>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <RdvFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        defaultDay={selectedDay || today}
        existing={editingRdv}
        allRdv={rendezVous}
        serviceArticles={serviceArticles}
        canAssignOthers={canAssignOthers}
        canSearchClients={canSearchClients}
        currentUser={user}
        onSaved={() => { setFormOpen(false); load(); }}
      />
    </div>
  );
}

function computeTotals(prestations) {
  return {
    duree: prestations.reduce((s, p) => s + (Number(p.duree_minutes) || 0), 0),
    prix: prestations.reduce((s, p) => s + (Number(p.prix_ttc) || 0), 0),
  };
}

// Exporté pour être réutilisé tel quel depuis l'onglet Vente (CaissePage.jsx,
// modèle "rendez_vous") quand on clique "Prendre un rendez-vous" sur une
// prestation — même formulaire complet (client, prestations, vendeur,
// détection de superposition...) qu'ici, plutôt qu'un mini-dialogue limité.
export function RdvFormDialog({ open, onOpenChange, defaultDay, existing, presetArticle, allRdv, serviceArticles, canAssignOthers, canSearchClients, currentUser, onSaved }) {
  const pad = (n) => String(n).padStart(2, "0");
  const isEdit = !!existing;

  const [date, setDate] = useState("");
  const [heure, setHeure] = useState("09:00");
  const [clientId, setClientId] = useState(null);
  const [clientNom, setClientNom] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [prestations, setPrestations] = useState([]);
  const [pickArticleId, setPickArticleId] = useState("");
  const [freeNom, setFreeNom] = useState("");
  const [freeDuree, setFreeDuree] = useState("");
  const [freePrix, setFreePrix] = useState("");
  const [dureeTotale, setDureeTotale] = useState("");
  const [prixTotal, setPrixTotal] = useState("");
  const [vendeurId, setVendeurId] = useState(currentUser?.id || currentUser?._id || "");
  const [shopUsers, setShopUsers] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Rendez-vous du même vendeur détectés en conflit d'horaire au moment de
  // la dernière tentative d'enregistrement — tant que non vide, le bouton
  // principal est remplacé par l'avertissement + bouton de confirmation.
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      const d = new Date(existing.date_heure);
      setDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
      setHeure(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
      setClientId(existing.client_id || null);
      setClientNom(existing.client_nom || "");
      setClientSearch("");
      setClientResults([]);
      setPrestations((existing.prestations || []).map((p, i) => ({ key: `existing-${i}-${p.article_id || p.nom}`, article_id: p.article_id || null, nom: p.nom, duree_minutes: p.duree_minutes || 0, prix_ttc: p.prix_ttc || 0 })));
      setDureeTotale(existing.duree_minutes != null ? String(existing.duree_minutes) : "");
      setPrixTotal(existing.prix_ttc != null ? Number(existing.prix_ttc).toFixed(2) : "");
      setVendeurId(existing.vendeur_id || currentUser?.id || currentUser?._id || "");
      setNotes(existing.notes || "");
    } else {
      setDate(`${defaultDay.getFullYear()}-${pad(defaultDay.getMonth() + 1)}-${pad(defaultDay.getDate())}`);
      setHeure("09:00");
      setClientId(null);
      setClientNom("");
      setClientSearch("");
      setClientResults([]);
      // Prestation pré-remplie (voir CaissePage.jsx > "Prendre un rendez-vous"
      // depuis un clic sur une prestation de l'onglet Vente) : même ligne
      // qu'un ajout normal depuis le stock (addStockPrestation), juste posée
      // d'avance plutôt qu'ajoutée par l'utilisateur.
      if (presetArticle) {
        const row = { key: `preset-${presetArticle.id}`, article_id: presetArticle.id, nom: presetArticle.nom, duree_minutes: presetArticle.duree_minutes || 0, prix_ttc: presetArticle.prix_ttc ?? presetArticle.prix ?? 0 };
        setPrestations([row]);
        setDureeTotale(String(row.duree_minutes));
        setPrixTotal(Number(row.prix_ttc).toFixed(2));
      } else {
        setPrestations([]);
        setDureeTotale(""); setPrixTotal("");
      }
      setPickArticleId("");
      setFreeNom(""); setFreeDuree(""); setFreePrix("");
      setVendeurId(currentUser?.id || currentUser?._id || "");
      setNotes("");
    }
    setConflicts([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing, defaultDay, presetArticle]);

  // Tout changement touchant l'horaire ou le vendeur invalide un avertissement
  // de superposition déjà affiché — il doit être recalculé, pas laissé tel quel.
  useEffect(() => { setConflicts([]); }, [date, heure, vendeurId, dureeTotale]);

  useEffect(() => {
    if (!open || !canAssignOthers) return;
    api.get("/users").then((r) => setShopUsers((r.data || []).filter((u) => u.shop_id === currentUser?.effective_shop_id))).catch(() => {});
  }, [open, canAssignOthers, currentUser]);

  useEffect(() => {
    if (!canSearchClients || !clientSearch.trim()) { setClientResults([]); return; }
    const t = setTimeout(() => {
      api.get("/clients", { params: { q: clientSearch } }).then((r) => setClientResults(r.data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [clientSearch, canSearchClients]);

  const addStockPrestation = (articleId) => {
    const art = (serviceArticles || []).find((a) => a.id === articleId);
    if (!art) return;
    const row = { key: `${art.id}-${Date.now()}`, article_id: art.id, nom: art.nom, duree_minutes: art.duree_minutes || 0, prix_ttc: art.prix_ttc || 0 };
    const next = [...prestations, row];
    setPrestations(next);
    const totals = computeTotals(next);
    setDureeTotale(String(totals.duree));
    setPrixTotal(totals.prix.toFixed(2));
    setPickArticleId("");
  };

  const addFreePrestation = () => {
    if (!freeNom.trim()) { toast.error("Indiquez le nom de la prestation"); return; }
    const row = { key: `libre-${Date.now()}`, article_id: null, nom: freeNom.trim(), duree_minutes: Number(freeDuree) || 0, prix_ttc: Number(String(freePrix).replace(",", ".")) || 0 };
    const next = [...prestations, row];
    setPrestations(next);
    const totals = computeTotals(next);
    setDureeTotale(String(totals.duree));
    setPrixTotal(totals.prix.toFixed(2));
    setFreeNom(""); setFreeDuree(""); setFreePrix("");
  };

  const removePrestation = (key) => {
    const next = prestations.filter((p) => p.key !== key);
    setPrestations(next);
    const totals = computeTotals(next);
    setDureeTotale(next.length ? String(totals.duree) : "");
    setPrixTotal(next.length ? totals.prix.toFixed(2) : "");
  };

  const buildPayload = () => {
    const clientLabel = clientId ? clientNom : (clientNom.trim() || clientSearch.trim());
    const titre = [clientLabel, prestations.map((p) => p.nom).join(", ")].filter(Boolean).join(" — ") || "Rendez-vous";
    return {
      titre,
      date_heure: new Date(`${date}T${heure}`).toISOString(),
      notes: notes.trim(),
      client_id: clientId,
      client_nom: clientId ? null : (clientLabel || null),
      prestations: prestations.map((p) => ({ article_id: p.article_id, nom: p.nom, duree_minutes: Number(p.duree_minutes) || 0, prix_ttc: Number(p.prix_ttc) || 0 })),
      duree_minutes: dureeTotale !== "" ? Number(dureeTotale) : null,
      prix_ttc: prixTotal !== "" ? Number(String(prixTotal).replace(",", ".")) : null,
      vendeur_id: vendeurId || undefined,
      clientLabel,
    };
  };

  const doSubmit = async (force) => {
    if (!date || !heure) { toast.error("Indiquez une date et une heure"); return; }
    const payload = buildPayload();
    if (!payload.clientLabel && prestations.length === 0) { toast.error("Indiquez au moins un client ou une prestation"); return; }

    if (!force) {
      const found = findConflicts({ date_heure: payload.date_heure, duree_minutes: payload.duree_minutes || 0, vendeur_id: vendeurId }, allRdv, existing?.id);
      if (found.length > 0) { setConflicts(found); return; }
    }

    const { clientLabel, ...body } = payload;
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.patch(`/rendez-vous/${existing.id}`, body);
        toast.success("Rendez-vous modifié");
      } else {
        await api.post("/rendez-vous", body);
        toast.success("Rendez-vous ajouté");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="prise-rdv-add-dialog">
        <DialogHeader><DialogTitle>{isEdit ? "Modifier le rendez-vous" : "Ajouter un rendez-vous"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="prise-rdv-date" />
            <Input type="time" value={heure} onChange={(e) => setHeure(e.target.value)} data-testid="prise-rdv-heure" />
          </div>

          <div>
            <p className="text-sm text-muted-foreground mb-1.5 flex items-center gap-1.5"><Contact size={13} /> Client</p>
            {clientId ? (
              <div className="flex items-center justify-between gap-2 rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm" data-testid="prise-rdv-selected-client">
                <span className="truncate">{clientNom}</span>
                <button onClick={() => { setClientId(null); setClientNom(""); }} data-testid="prise-rdv-client-clear"><X size={14} /></button>
              </div>
            ) : canSearchClients ? (
              <div className="relative">
                <Input
                  placeholder="Rechercher un client, ou saisir un nom libre..."
                  value={clientSearch || clientNom}
                  onChange={(e) => { setClientSearch(e.target.value); setClientNom(e.target.value); setClientDropdownOpen(true); }}
                  onFocus={() => setClientDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setClientDropdownOpen(false), 150)}
                  data-testid="prise-rdv-client-search"
                />
                {clientDropdownOpen && clientResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-border bg-card shadow-md" data-testid="prise-rdv-client-results">
                    {clientResults.map((c) => (
                      <button
                        key={c.id}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-accent"
                        data-testid={`prise-rdv-client-option-${c.id}`}
                        onMouseDown={() => { setClientId(c.id); setClientNom(`${c.prenom || ""} ${c.nom || ""}`.trim()); setClientSearch(""); setClientResults([]); }}
                      >
                        {c.prenom} {c.nom} {c.telephone ? `· ${c.telephone}` : ""}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Input placeholder="Nom du client" value={clientNom} onChange={(e) => setClientNom(e.target.value)} data-testid="prise-rdv-client-free" />
            )}
          </div>

          <div>
            <p className="text-sm text-muted-foreground mb-1.5 flex items-center gap-1.5"><Scissors size={13} /> Prestation(s)</p>
            {prestations.length > 0 && (
              <div className="space-y-1 mb-2">
                {prestations.map((p) => (
                  <div key={p.key} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-1.5 text-sm">
                    <span className="truncate">{p.nom} <span className="text-muted-foreground">· {p.duree_minutes} min · {Number(p.prix_ttc).toFixed(2)} €</span></span>
                    <button onClick={() => removePrestation(p.key)} data-testid={`prise-rdv-prestation-remove-${p.key}`}><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
            {serviceArticles.length > 0 && (
              <Select value={pickArticleId} onValueChange={addStockPrestation}>
                <SelectTrigger data-testid="prise-rdv-pick-article"><SelectValue placeholder="Ajouter une prestation du stock..." /></SelectTrigger>
                <SelectContent>
                  {serviceArticles.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.nom} — {a.duree_minutes || 0} min — {Number(a.prix_ttc || 0).toFixed(2)} €</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="flex items-end gap-2 mt-2">
              <div className="flex-1">
                <Input placeholder="Prestation libre" value={freeNom} onChange={(e) => setFreeNom(e.target.value)} data-testid="prise-rdv-free-nom" />
              </div>
              <Input type="number" placeholder="min" className="w-20" value={freeDuree} onChange={(e) => setFreeDuree(e.target.value)} data-testid="prise-rdv-free-duree" />
              <Input placeholder="€" className="w-20" value={freePrix} onChange={(e) => setFreePrix(e.target.value)} data-testid="prise-rdv-free-prix" />
              <Button type="button" variant="outline" size="icon" onClick={addFreePrestation} data-testid="prise-rdv-free-add"><Plus size={14} /></Button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Durée totale (min)</p>
              <Input type="number" value={dureeTotale} onChange={(e) => setDureeTotale(e.target.value)} data-testid="prise-rdv-duree-totale" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Prix total (€)</p>
              <Input value={prixTotal} onChange={(e) => setPrixTotal(e.target.value)} data-testid="prise-rdv-prix-total" />
            </div>
          </div>

          <div>
            <p className="text-sm text-muted-foreground mb-1.5 flex items-center gap-1.5"><User size={13} /> Vendeur</p>
            {canAssignOthers ? (
              <Select value={vendeurId} onValueChange={setVendeurId}>
                <SelectTrigger data-testid="prise-rdv-vendeur-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {shopUsers.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.prenom} {u.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <div className="rounded-lg border border-border bg-secondary/50 px-3 py-2 text-sm text-muted-foreground" data-testid="prise-rdv-vendeur-fixed">
                {currentUser?.prenom} {currentUser?.nom} (vous)
              </div>
            )}
          </div>

          <Textarea placeholder="Notes (optionnel)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="prise-rdv-notes" />

          {conflicts.length > 0 && (
            <div className="rounded-lg border border-violet-500/50 bg-violet-500/10 p-3 space-y-2" data-testid="prise-rdv-conflict-warning">
              <p className="flex items-center gap-1.5 text-sm font-medium text-violet-700 dark:text-violet-400">
                <AlertTriangle size={15} /> Attention, superposition de rendez-vous
              </p>
              <p className="text-xs text-muted-foreground">
                Ce vendeur a déjà {conflicts.length > 1 ? "des rendez-vous" : "un rendez-vous"} sur ce créneau :
                {" "}
                {conflicts.map((c) => `${new Date(c.date_heure).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} (${c.client_nom || c.titre})`).join(", ")}.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          {conflicts.length > 0 ? (
            <Button
              onClick={() => doSubmit(true)}
              disabled={submitting}
              className="bg-violet-600 hover:bg-violet-700 text-white"
              data-testid="prise-rdv-confirm-overlap"
            >
              {submitting ? "Enregistrement..." : "Je valide le rdv"}
            </Button>
          ) : (
            <Button onClick={() => doSubmit(false)} disabled={submitting} data-testid="prise-rdv-submit">
              {submitting ? "Enregistrement..." : isEdit ? "Enregistrer" : "Ajouter le rendez-vous"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
